<template>
  <modal
    id="deal-extend-modal"
    :is-opened="isOpened"
    :on-close="close"
    data-cy="adaf-deal-extend-modal"
  >
    <template #header>
      {{ trans('organization.business_input.deal_row.extend_modal.title') }}
    </template>
    <template #body>
      <div class="modal-state">
        <p>
          {{ trans('organization.business_input.deal_row.extend_modal.text', { fullname: dealBuyerFullname }) }}
          &nbsp;! {{ trans('organization.business_input.deal_row.extend_modal.confirm') }}
        </p>
        <p class="input-label">
          {{ trans('organization.business_input.deal_row.extend_modal.price_label') }}
        </p>
        <div class="input-text input-text--legend">
          <input ref="currencyInputElement" v-model="vuelidateState.price.$model" :class="{ error: vuelidateState.price.$error }">
          <span>{{ trans('global.currency_symbol') }} {{ trans('global.price_tax') }}</span>
        </div>
        <span
          v-if="vuelidateState.price.$error"
          class="input-text error-message"
        >
          {{ trans('organization.business_input.deal_row.extend_modal.price_error') }}
        </span>
        <p class="input-label input-end-date">
          {{ trans('organization.business_input.deal_row.extend_modal.contract_end') }}
        </p>
        <div class="datepicker">
          <vue-date-picker
            v-model="vuelidateState.endDate.$model"
            :min-date="disabledDates"
            :enable-time-picker="false"
            :monday-first="true"
            format="dd/MM/yyyy"
            name="date_end_deal"
            locale="fr"
            auto-apply
            @update:model-value="reinitUnlimitedQuantity"
          >
            <template #input-icon>
              <span class="datepicker__icon datepicker__icon--datepicker" />
            </template>
          </vue-date-picker>
        </div>
        <span
          v-if="vuelidateState.endDate.$error && !vuelidateState.endDate.required"
          class="input-text error-message"
        >
          {{ trans('organization.business_input.deal_row.extend_modal.quantity_error') }}
        </span>
        <div class="input-checkbox">
          <input
            id="unlimitedQuantity"
            v-model="unlimitedQuantity"
            type="checkbox"
            @click="reinitDate"
          >
          <label for="unlimitedQuantity">
            {{ trans('organization.business_input.deal_row.extend_modal.unlimited_quantity_label') }}
          </label>
        </div>
        <div class="modal-state__actions">
          <button
            class="btn btn--secondary"
            @click="close()"
          >
            {{ trans('global.cancel') }}
          </button>
          <button
            class="btn btn--primary modal-default-button confirm-button"
            :class="{ disabled: vuelidateState.$error, 'loading-btn': loading }"
            @click="ok"
          >
            <span>{{ trans('global.confirm') }}</span>
          </button>
        </div>
      </div>
    </template>
  </modal>
</template>

<script>
import { ref, reactive, toRefs, onMounted } from 'vue';
import axios from 'axios';
import { useVuelidate } from '@vuelidate/core';
import { required, maxLength, requiredIf, numeric } from '@vuelidate/validators';
import moment from 'moment';
import VueDatePicker from '@vuepic/vue-datepicker';
import { useCurrencyInput } from 'vue-currency-input';
import Modal from '@components/Modal.vue';
import { flashBagError, flashBagSuccess } from '@tools/flashbag';
import { requestsMixin } from '@tools/requests-mixin';
import { URL_ADAF_DEAL_EXTEND } from '@tools/routes/routes';
import { trans, translationMixin } from '@elements/translation';
import { eventBus } from '@tools/event-bus';
import { getNowToDisableDateForAdafModals } from './date-mixins';

export default {
  components: {
    Modal, VueDatePicker,
  },
  mixins: [ translationMixin, requestsMixin ],
  props: {
    isOpened: { type: Boolean, default: false },
    dealId: { type: Number, default: 0 },
    dealBuyerFullname: { type: String, default: '' },
  },
  setup(props, { emit }) {
    const formState = reactive({
      price: null,
      endDate: null,
      unlimitedQuantity: false,
    });
    const disabledDates = getNowToDisableDateForAdafModals();

    const currencyInputElement = ref(null);

    useCurrencyInput(currencyInputElement, {
      currency: 'EUR',
      locale: 'fr-FR',
      precision: 2,
      allowNegative: false,
      distractionFree: false,
      valueAsInteger: true,
      autoDecimalMode: true,
    });

    const vuelidateState = useVuelidate({
      price: { required, numeric, maxLength: maxLength(10) },
      endDate: { required: requiredIf(() => !formState.unlimitedQuantity) },
      unlimitedQuantity: {},
    }, formState);

    onMounted(() => {
      if (currencyInputElement.value) {
        currencyInputElement.value.focus();
      }
    });

    const loading = ref(false);

    const close = () => {
      formState.price = null;
      formState.endDate = null;
      formState.unlimitedQuantity = false;
      loading.value = false;
      emit('close');
      vuelidateState.value.$reset();
    };

    const reinitDate = () => {
      formState.endDate = null;
    };

    const reinitUnlimitedQuantity = () => {
      if (formState.endDate) {
        formState.unlimitedQuantity = false;
      }
    };

    const ok = async () => {
      vuelidateState.value.$touch();
      if (vuelidateState.value.$error) return;
      if (loading.value) return;

      loading.value = true;

      const response = await axios.post(URL_ADAF_DEAL_EXTEND.replace('{id}', props.dealId), {
        price: formState.price,
        unlimited: formState.unlimitedQuantity,
        endDate: formState.endDate ? moment(formState.endDate.toISOString()).format('YYYY-MM-DD') : null,
      });
      loading.value = false;

      if (response.status === 200) {
        eventBus.dispatchEvent(new CustomEvent('deal-extended', {
          detail: {
            dealId: props.dealId,
            quantity: response.data.quantity,
            endDate: formState.endDate,
          },
        }));
        flashBagSuccess(trans('organization.business_input.deal_row.extend_modal.success_message'));
        close();
        return;
      }
      flashBagError();
    };

    return {
      ...toRefs(formState),
      loading,
      disabledDates,
      currencyInputElement,
      vuelidateState,
      close,
      reinitDate,
      reinitUnlimitedQuantity,
      ok,
    };
  },
};
</script>
